<template>
  <div
      v-for="alarm in activeAlarms"
      :key="alarm.id"
      ref="draggableCardRefs"
      :style="{
        top: alarm.top + 'px',
        left: alarm.left + 'px',
        width: '400px',
        zIndex: alarm.zIndex
      }"
      class="draggable-card"
      @mousedown="(e) => startDrag(e, alarm)"
  >
    <v-card>
      <v-card-title class="text-h5 text-center draggable-header">Warning</v-card-title>
      <v-card-text class="d-flex align-center justify-space-between">
        <v-icon class="mr-2" color="red" size="48">mdi-alert</v-icon>
        <span class="text-center flex-grow-1">
            {{ alarm.description }} ({{ alarm.category }} - {{alarm.no}}) on Train {{ alarm.trainNumber }} ({{ alarm.detectionTarget}}) <br/>
            {{ new Date(alarm.timestamp).toLocaleDateString() }} -
            {{ new Date(alarm.timestamp).toLocaleTimeString() }}
          </span>
      </v-card-text>
      <v-card-actions class="justify-center">
        <v-btn class="bg-grey-lighten-2 text-black" @click="acknowledgeAlarm(alarm)">
          Acknowledge
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup>
import {ref, onMounted, onUnmounted} from 'vue';
import {useAlarmsStore} from "@/stores/alarmsStore";
import api from '@/services/api'
import {keycloak} from "@/main";
import {EventSourcePolyfill} from "event-source-polyfill";

const activeAlarms = ref([]);
const alarmStore = useAlarmsStore();

let eventSource;
let audioContext;
let audioBuffer;
let zIndexCounter = 10000;

const acknowledgeAlarm = (alarm) => {
  alarmStore.acknowledge(alarm.id);
  activeAlarms.value = activeAlarms.value.filter((a) => a.id !== alarm.id);
};

const loadAudio = async () => {
  audioContext = new AudioContext();
  const response = await fetch('/audio/alarm.wav');
  const arrayBuffer = await response.arrayBuffer();
  audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
};

const playSound = () => {
  if (audioContext?.state === 'suspended') {
    audioContext.resume();
  }

  const source = audioContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(audioContext.destination);
  source.start(0);
};

function subscribeToAlarmEvents() {
  const baseUrl = api.defaults.baseURL
  const token = keycloak?.token
  eventSource = new EventSourcePolyfill(baseUrl + "/alarms/subscribe", {
    heartbeatTimeout: Number.MAX_SAFE_INTEGER,
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });
  eventSource.addEventListener("alarm-event", (event) => {
    try {
      const alarmData = JSON.parse(event.data);
      const alarmType = alarmData.category;
      if (!alarmStore.popupConfig[alarmType]) {
        alarmStore.alarmsHistory.push(alarmData);
        if (!alarmStore.alarmHistoryActive)
          alarmStore.lostAlarm = true;
        return;
      }
      const dialogWidth = 400;
      const dialogHeight = 200;

      const centerX = (window.innerWidth - dialogWidth) / 2;
      const centerY = (window.innerHeight - dialogHeight) / 2;

      const offset = activeAlarms.value.length * 20;

      let proposedLeft = centerX + offset;
      let proposedTop = centerY + offset;

      const maxX = window.innerWidth - dialogWidth;
      const maxY = window.innerHeight - dialogHeight;

      const isOutOfBounds = (
          proposedLeft < 0 || proposedLeft > maxX ||
          proposedTop < 0 || proposedTop > maxY
      );

      if (isOutOfBounds) {
        const angle = Math.random() * 2 * Math.PI;
        const radius = Math.random() * 150;
        proposedLeft = centerX + Math.cos(angle) * radius;
        proposedTop = centerY + Math.sin(angle) * radius;

        proposedLeft = Math.max(0, Math.min(proposedLeft, maxX));
        proposedTop = Math.max(0, Math.min(proposedTop, maxY));
      }

      activeAlarms.value.push({
        ...alarmData,
        id: alarmData.id,
        top: proposedTop,
        left: proposedLeft,
        zIndex: zIndexCounter++,
      });

      playSound();
    } catch (e) {
      console.error("Failed to parse alarm event:", e);
    }
  });

  eventSource.onerror = (err) => {
    console.error("SSE connection error on alarms event listener: ", err, " attempting to reconnect in 5 seconds ...");
    eventSource.close();
    setTimeout(() => {
      subscribeToAlarmEvents();
    }, 5000);
  };
}

onMounted(() => {
  alarmStore.getDefaultPopupConfig();
  loadAudio();
  subscribeToAlarmEvents()
});

onUnmounted(() => {
  if (eventSource) eventSource.close();
});

// Draggable functionality
const draggingAlarm = ref(null);
const offset = ref({x: 0, y: 0});

const startDrag = (e, alarm) => {
  // Bring to top
  alarm.zIndex = zIndexCounter++;

  draggingAlarm.value = alarm;
  offset.value = {
    x: e.clientX - alarm.left,
    y: e.clientY - alarm.top,
  };

  document.addEventListener("mousemove", onDrag);
  document.addEventListener("mouseup", stopDrag);
};

const onDrag = (e) => {
  if (!draggingAlarm.value) return;

  const dialogWidth = 400;
  const dialogHeight = 200;

  const maxX = window.innerWidth - dialogWidth;
  const maxY = window.innerHeight - dialogHeight;

  let newX = e.clientX - offset.value.x;
  let newY = e.clientY - offset.value.y;

  newX = Math.max(0, Math.min(newX, maxX));
  newY = Math.max(0, Math.min(newY, maxY));

  draggingAlarm.value.left = newX;
  draggingAlarm.value.top = newY;
};

const stopDrag = () => {
  draggingAlarm.value = null;
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
};
</script>

<style scoped>
.draggable-card {
  position: fixed;
  top: 100px;
  left: 100px;
  width: 400px;
  cursor: grab;
  user-select: none;
  box-shadow: 0 2px 10px darkred;
}

.draggable-header {
  cursor: move;
}

.draggable-dialog-wrapper {
  overflow: visible !important;
  background: transparent;
  box-shadow: none;
}
</style>
